import React, { useEffect, useState } from "react";
import { Shell } from "../components/Shell";
import { Tabs } from "../components/Tabs";
import { api, ApiError, descargarArchivo } from "../api";
import { useAuth } from "../auth/AuthContext";

type ReporteExogena = {
  id: number; formato: string; nombre: string; anioGravable: number; cantidadRegistros: number;
  valorTotal: number; estado: string; generadoEn: string;
};
type Declaracion = {
  id: number; tipo: string; periodo: string; fechaInicio: string; fechaFin: string; detalle: any;
  totalAPagar: number; estado: string; generadoEn: string;
};

const money = (n: number) => `$${Math.round(n).toLocaleString()}`;
const TIPO_DECLARACION_LABEL: Record<string, string> = { iva: "IVA", retefuente: "Retención en la fuente (Formulario 350)", ica: "Industria y Comercio (ICA)" };
const esTarifa = (clave: string) => /tarifa/i.test(clave);
const formatearValor = (clave: string, valor: number) => (esTarifa(clave) ? `${valor}‰` : money(valor));
const ETIQUETA_FORMATO: Record<string, string> = { pdf: "PDF", xlsx: "Excel", docx: "Word" };

// Descarga en PDF/Excel/Word de la versión legible de un reporte de
// Cumplimiento (información exógena o declaración) — no reemplaza el .txt
// plano que exige el prevalidador DIAN, es para revisión interna o entrega
// al contador. Mismo motor genérico que usan Nómina y Contabilidad.
function DescargaReporte({ endpoint, nombreBase, formatos = ["pdf", "xlsx", "docx"], label = "Descargar" }: {
  endpoint: string; nombreBase: string; formatos?: string[]; label?: string;
}) {
  const [formato, setFormato] = useState(formatos[0]);
  const [descargando, setDescargando] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const descargar = async () => {
    setDescargando(true);
    setError(null);
    try {
      await descargarArchivo(`${endpoint}?formato=${formato}`, `${nombreBase}.${formato}`);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "No se pudo generar el reporte.");
    } finally {
      setDescargando(false);
    }
  };

  return (
    <span className="inline-flex items-center gap-2">
      <select
        className="rounded-lg border border-borderc bg-white px-2 py-1.5 text-xs text-slate-700 focus:outline-none focus:ring-4 focus:ring-accent/15 focus:border-accent"
        value={formato} onChange={(e) => setFormato(e.target.value)}
      >
        {formatos.map((f) => <option key={f} value={f}>{ETIQUETA_FORMATO[f] || f}</option>)}
      </select>
      <button className="text-navy hover:underline text-xs font-semibold disabled:opacity-40" disabled={descargando} onClick={descargar}>
        {descargando ? "Generando..." : label}
      </button>
      {error && <span className="text-xs text-red-600">{error}</span>}
    </span>
  );
}

export function Cumplimiento() {
  const { tienePermiso } = useAuth();
  const puedeGenerar = tienePermiso("cumplimiento:generar");
  const puedePresentar = tienePermiso("cumplimiento:presentar");

  const [tab, setTab] = useState("exogena");

  return (
    <Shell titulo="Cumplimiento DIAN" breadcrumb="Cumplimiento DIAN Ampliado">
      <p className="text-sm text-textmuted mb-4">
        Información exógena (formatos 1001, 1003, 1005-1009 y 2276) generada a partir de las retenciones, comprobantes
        contables y liquidaciones de nómina ya registrados, y declaraciones tributarias en borrador (IVA, Retefuente, ICA)
        listas para transcribir al portal MUISCA o del municipio.
      </p>
      <div className="rounded-md bg-amber-50 border border-amber-200 px-3 py-2 text-xs text-amber-800 mb-4">
        Estructura de referencia: los formatos y tarifas cambian cada año gravable según la resolución DIAN vigente — un
        contador debe validarlos contra el prevalidador tributario del año correspondiente antes de presentarlos.
      </div>
      <Tabs
        tabs={[
          { key: "exogena", label: "Información exógena" },
          { key: "declaraciones", label: "Declaraciones tributarias" },
        ]}
        activo={tab} onChange={setTab}
      />
      {tab === "exogena" && <TabExogena puedeGenerar={puedeGenerar} puedePresentar={puedePresentar} />}
      {tab === "declaraciones" && <TabDeclaraciones puedeGenerar={puedeGenerar} puedePresentar={puedePresentar} />}
    </Shell>
  );
}

// ---------------------------------------------------------------- información exógena
function TabExogena({ puedeGenerar, puedePresentar }: { puedeGenerar: boolean; puedePresentar: boolean }) {
  const [anio, setAnio] = useState(new Date().getFullYear());
  const [formatosDisponibles, setFormatosDisponibles] = useState<{ formato: string; nombre: string }[]>([]);
  const [reportes, setReportes] = useState<ReporteExogena[]>([]);
  const [cargando, setCargando] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [generando, setGenerando] = useState<string | null>(null);
  const [detalle, setDetalle] = useState<{ id: number; formato: string; filas: any[] } | null>(null);

  const cargar = async () => {
    setCargando(true);
    setError(null);
    try {
      const [formatos, reps] = await Promise.all([
        api.get("/cumplimiento/exogena/formatos"),
        api.get(`/cumplimiento/exogena?anioGravable=${anio}`),
      ]);
      setFormatosDisponibles(formatos); setReportes(reps);
    } catch (e) {
      setError(e instanceof ApiError ? e.message : "No se pudo cargar la información exógena.");
    } finally {
      setCargando(false);
    }
  };
  useEffect(() => { cargar(); }, [anio]);

  const generar = async (formato: string) => {
    setGenerando(formato);
    try {
      const res = await api.post(`/cumplimiento/exogena/${formato}/generar`, { anioGravable: anio });
      setDetalle({ id: res.id, formato, filas: res.filas });
      cargar();
    } catch (e) {
      setError(e instanceof ApiError ? e.message : `No se pudo generar el formato ${formato}.`);
    } finally {
      setGenerando(null);
    }
  };

  const verDetalle = (id: number, formato: string) => {
    api.get(`/cumplimiento/exogena/${id}`).then(() => { /* datos ya en `reportes`; se abre el resumen */ });
    setDetalle({ id, formato, filas: [] });
  };

  const presentar = async (id: number) => {
    try { await api.post(`/cumplimiento/exogena/${id}/presentar`, {}); cargar(); } catch { /* noop */ }
  };

  const reportePorFormato = (formato: string) => reportes.find((r) => r.formato === formato);

  return (
    <div>
      <div className="flex justify-between items-end mb-3">
        <div className="max-w-[140px] w-full">
          <label className="block text-xs font-semibold text-slate-600 mb-1">Año gravable</label>
          <input className="input-field" type="number" value={anio} onChange={(e) => setAnio(Number(e.target.value))} />
        </div>
      </div>
      {error && <div className="rounded-md bg-red-50 border border-red-200 px-3 py-2 text-sm text-red-700 mb-3">{error}</div>}
      {cargando ? (
        <p className="text-sm text-textmuted">Cargando...</p>
      ) : (
        <div className="card overflow-hidden">
          <table className="w-full">
            <thead><tr className="border-b border-borderc">
              <th className="table-th">Formato</th><th className="table-th">Descripción</th><th className="table-th">Registros</th>
              <th className="table-th">Valor total</th><th className="table-th">Estado</th><th className="table-th"></th>
            </tr></thead>
            <tbody>
              {formatosDisponibles.map((f) => {
                const rep = reportePorFormato(f.formato);
                return (
                  <tr key={f.formato} className="border-b border-borderc last:border-0">
                    <td className="table-td font-mono text-xs font-semibold">{f.formato}</td>
                    <td className="table-td">{f.nombre}</td>
                    <td className="table-td">{rep ? rep.cantidadRegistros : "—"}</td>
                    <td className="table-td">{rep ? money(rep.valorTotal) : "—"}</td>
                    <td className="table-td">
                      {rep ? (
                        <span className={`badge ${rep.estado === "presentado" ? "bg-accent-light text-accent-dark" : "bg-amber-50 text-amber-700"}`}>{rep.estado}</span>
                      ) : <span className="text-textmuted text-xs">Sin generar</span>}
                    </td>
                    <td className="table-td text-right space-x-2">
                      {puedeGenerar && (
                        <button className="text-accent-dark hover:underline text-xs font-semibold" onClick={() => generar(f.formato)} disabled={generando === f.formato}>
                          {generando === f.formato ? "Generando..." : rep ? "Regenerar" : "Generar"}
                        </button>
                      )}
                      {rep && (
                        <button
                          className="text-navy hover:underline text-xs font-semibold"
                          onClick={() => descargarArchivo(`/cumplimiento/exogena/${rep.id}/descargar`, `${f.formato}_${rep.anioGravable}.txt`)}
                        >
                          Descargar .txt
                        </button>
                      )}
                      {rep && (
                        <DescargaReporte
                          endpoint={`/cumplimiento/exogena/${rep.id}/exportar`} nombreBase={`${f.formato}_${rep.anioGravable}`} label="Legible"
                        />
                      )}
                      {rep && puedePresentar && rep.estado === "generado" && (
                        <button className="text-red-600 hover:underline text-xs font-semibold" onClick={() => presentar(rep.id)}>Marcar presentado</button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {detalle && detalle.filas.length > 0 && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 px-4">
          <div className="card w-full max-w-2xl p-6 space-y-3 max-h-[85vh] overflow-y-auto">
            <h3 className="text-base font-semibold text-slate-800">Formato {detalle.formato} — vista previa</h3>
            <div className="overflow-x-auto">
              <table className="w-full text-xs">
                <thead><tr className="border-b border-borderc">
                  {Object.keys(detalle.filas[0]).map((k) => <th key={k} className="table-th whitespace-nowrap">{k}</th>)}
                </tr></thead>
                <tbody>
                  {detalle.filas.map((f, i) => (
                    <tr key={i} className="border-b border-borderc last:border-0">
                      {Object.values(f).map((v: any, j) => <td key={j} className="table-td whitespace-nowrap">{typeof v === "number" ? money(v) : String(v)}</td>)}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="flex justify-end"><button className="btn-primary" onClick={() => setDetalle(null)}>Cerrar</button></div>
          </div>
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------- declaraciones
function TabDeclaraciones({ puedeGenerar, puedePresentar }: { puedeGenerar: boolean; puedePresentar: boolean }) {
  const [declaraciones, setDeclaraciones] = useState<Declaracion[]>([]);
  const [cargando, setCargando] = useState(true);
  const [modalAbierto, setModalAbierto] = useState(false);

  const cargar = async () => {
    setCargando(true);
    try { setDeclaraciones(await api.get("/cumplimiento/declaraciones")); } finally { setCargando(false); }
  };
  useEffect(() => { cargar(); }, []);

  const presentar = async (id: number) => {
    try { await api.post(`/cumplimiento/declaraciones/${id}/presentar`, {}); cargar(); } catch { /* noop */ }
  };

  return (
    <div>
      <div className="flex justify-end mb-3">
        {puedeGenerar && <button className="btn-primary" onClick={() => setModalAbierto(true)}>+ Calcular declaración</button>}
      </div>
      {cargando ? (
        <p className="text-sm text-textmuted">Cargando...</p>
      ) : (
        <div className="space-y-4">
          {declaraciones.map((d) => (
            <div key={d.id} className="card p-5">
              <div className="flex justify-between items-start mb-3">
                <div>
                  <h3 className="text-sm font-semibold text-slate-800">{TIPO_DECLARACION_LABEL[d.tipo] || d.tipo} — {d.periodo}</h3>
                  <p className="text-xs text-textmuted">{d.fechaInicio} a {d.fechaFin}</p>
                </div>
                <div className="text-right space-y-1">
                  <span className={`badge ${d.estado === "presentada" ? "bg-accent-light text-accent-dark" : "bg-amber-50 text-amber-700"}`}>{d.estado}</span>
                  <div><DescargaReporte endpoint={`/cumplimiento/declaraciones/${d.id}/exportar`} nombreBase={`declaracion_${d.tipo}_${d.periodo}`} formatos={["pdf", "xlsx", "docx"]} label="Descargar" /></div>
                  {puedePresentar && d.estado === "borrador" && (
                    <button className="block text-red-600 hover:underline text-xs font-semibold" onClick={() => presentar(d.id)}>Marcar presentada</button>
                  )}
                </div>
              </div>
              <table className="w-full text-sm mb-3">
                <tbody>
                  {Object.entries(d.detalle).map(([k, v]) => (
                    typeof v === "object" && v !== null ? (
                      Object.entries(v as Record<string, number>).map(([k2, v2]) => (
                        <tr key={`${k}-${k2}`} className="border-b border-borderc last:border-0">
                          <td className="table-td capitalize">{k2.replace(/_/g, " ")}</td><td className="table-td">{formatearValor(k2, v2)}</td>
                        </tr>
                      ))
                    ) : (
                      <tr key={k} className="border-b border-borderc last:border-0">
                        <td className="table-td capitalize">{k.replace(/([A-Z])/g, " $1")}</td>
                        <td className="table-td">{typeof v === "number" ? formatearValor(k, v) : String(v)}</td>
                      </tr>
                    )
                  ))}
                </tbody>
              </table>
              <div className="rounded-md bg-navy/5 border border-navy/10 px-3 py-2 text-sm font-semibold flex justify-between text-navy">
                <span>Total a pagar</span><span>{money(d.totalAPagar)}</span>
              </div>
            </div>
          ))}
          {declaraciones.length === 0 && <p className="text-sm text-textmuted text-center py-8">Sin declaraciones calculadas aún.</p>}
        </div>
      )}
      {modalAbierto && <ModalDeclaracion onClose={() => setModalAbierto(false)} onCreada={() => { setModalAbierto(false); cargar(); }} />}
    </div>
  );
}

function ModalDeclaracion({ onClose, onCreada }: { onClose: () => void; onCreada: () => void }) {
  const hoy = new Date();
  const primerDia = new Date(hoy.getFullYear(), hoy.getMonth(), 1).toISOString().slice(0, 10);
  const ultimoDia = new Date(hoy.getFullYear(), hoy.getMonth() + 1, 0).toISOString().slice(0, 10);
  const [tipo, setTipo] = useState("retefuente");
  const [periodo, setPeriodo] = useState(`${hoy.getFullYear()}-${String(hoy.getMonth() + 1).padStart(2, "0")}`);
  const [fechaInicio, setFechaInicio] = useState(primerDia);
  const [fechaFin, setFechaFin] = useState(ultimoDia);
  const [error, setError] = useState<string | null>(null);
  const [enviando, setEnviando] = useState(false);

  const calcular = async (e: React.FormEvent) => {
    e.preventDefault();
    setEnviando(true);
    setError(null);
    try {
      await api.post("/cumplimiento/declaraciones/calcular", { tipo, periodo, fechaInicio, fechaFin });
      onCreada();
    } catch (e) {
      setError(e instanceof ApiError ? e.message : "No se pudo calcular la declaración.");
    } finally {
      setEnviando(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 px-4">
      <form onSubmit={calcular} className="card w-full max-w-md p-6 space-y-4">
        <h3 className="text-base font-semibold text-slate-800">Calcular declaración tributaria</h3>
        <div><label className="block text-xs font-semibold text-slate-600 mb-1">Tipo</label>
          <select className="input-field" value={tipo} onChange={(e) => setTipo(e.target.value)}>
            <option value="retefuente">Retención en la fuente (Formulario 350)</option>
            <option value="iva">IVA</option>
            <option value="ica">Industria y Comercio (ICA)</option>
          </select></div>
        <div><label className="block text-xs font-semibold text-slate-600 mb-1">Período (etiqueta, ej. 2026-08)</label>
          <input className="input-field" value={periodo} onChange={(e) => setPeriodo(e.target.value)} required /></div>
        <div className="grid grid-cols-2 gap-3">
          <div><label className="block text-xs font-semibold text-slate-600 mb-1">Desde</label>
            <input className="input-field" type="date" value={fechaInicio} onChange={(e) => setFechaInicio(e.target.value)} required /></div>
          <div><label className="block text-xs font-semibold text-slate-600 mb-1">Hasta</label>
            <input className="input-field" type="date" value={fechaFin} onChange={(e) => setFechaFin(e.target.value)} required /></div>
        </div>
        {error && <div className="rounded-md bg-red-50 border border-red-200 px-3 py-2 text-sm text-red-700">{error}</div>}
        <div className="flex justify-end gap-2 pt-2">
          <button type="button" className="btn-ghost" onClick={onClose}>Cancelar</button>
          <button type="submit" className="btn-primary" disabled={enviando}>{enviando ? "Calculando..." : "Calcular"}</button>
        </div>
      </form>
    </div>
  );
}
